import React, { useState, useEffect } from "react";
import { Box, Typography, Button, Switch, Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Divider } from "@mui/material";
import RestaurantIcon from "@mui/icons-material/Restaurant";
import { NoMeals } from "@mui/icons-material";
import { SubmitPoll, EditPoll, GetPollWithUserIdPayload, GetPollWithUserId } from "../../utils/APIRequest";
import { STORAGE_KEYS } from "../../utils/StorageKeys";
import { useUserContext } from "../../context/UserContext";
import { User } from "../../screens/MainScreen";

interface PollResponse {
    emp_name: string;
    foodChoice: string;
    pollDate: string;
    username: string;
}
interface LunchPollCardProps {
    isVotingAllowed: () => boolean;
    onPollSubmit?: (poll: PollResponse) => void;
}

const LunchPollCard: React.FC<LunchPollCardProps> = ({ isVotingAllowed, onPollSubmit }) => {
    const [attending, setAttending] = useState<boolean>(true);
    const [preference, setPreference] = useState<"Veg" | "Non-Veg">("Veg");
    const [error, setError] = useState<string>("");
    const [submitted, setSubmitted] = useState<boolean>(false);
    const [currentUserPoll, setCurrentUserPoll] = useState<PollResponse | null>(null);
    const [userHasVoted, setUserHasVoted] = useState<boolean>(false);
    const { currentUser } = useUserContext();



    // Fetch poll for current user on mount
    useEffect(() => {
        console.log('Fetching current user poll...');
        fetchCurrentUserPoll()
    }, []);

    const fetchCurrentUserPoll = async () => {
        const user = localStorage.getItem(STORAGE_KEYS.USER);
        if (user) {
            const parsedUser: User = JSON.parse(user);
            const tomorrow = new Date();
            tomorrow.setDate(tomorrow.getDate() + 1);
            const pollDate = tomorrow.toISOString().split("T")[0];
            const pollPayload: GetPollWithUserIdPayload = {
                date: pollDate,
                username: parsedUser.id,
            }
            console.log('Poll Payload:', pollPayload);
            const response = await GetPollWithUserId(pollPayload);
            console.log('Current User Poll Response:', response);
            if (Array.isArray(response) && response.length > 0) {
                const poll = response[0];
                setCurrentUserPoll(poll);
                setUserHasVoted(true);
                setSubmitted(true);
                if (poll.foodChoice === "No") {
                    setAttending(false);
                } else {
                    setAttending(true);
                    setPreference(poll.foodChoice === "Non-Veg" ? "Non-Veg" : "Veg");
                }
            } else {
                setCurrentUserPoll(null);
                setUserHasVoted(false);
                setSubmitted(false);
                setAttending(true);
                setPreference("Veg");
            }
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (attending && !preference) {
            setError("Please select your meal preference");
            return;
        }

        // Get user info from localStorage
        const userStr = localStorage.getItem(STORAGE_KEYS.USER);
        let username = "";
        let emp_name = "";
        if (userStr) {
            try {
                const userObj = JSON.parse(userStr);
                username = userObj.username || userObj.user_name || "";
                emp_name = userObj.emp_name || userObj.name || "";
            } catch (err) {
                setError("User info not found");
                return;
            }
        } else {
            setError("User info not found");
            return;
        }

        const tomorrow = new Date();
        tomorrow.setDate(tomorrow.getDate() + 1);
        const pollDate = tomorrow.toISOString().split("T")[0];
        const foodChoice = attending ? preference : "No";

        try {
            if (submitted) {
                // Update existing poll
                await EditPoll({ foodChoice, pollDate });
                onPollSubmit?.({ emp_name, foodChoice, pollDate, username });
            } else {
                // Submit new poll
                await SubmitPoll({ username, emp_name, foodChoice, pollDate });
                onPollSubmit?.({ emp_name, foodChoice, pollDate, username });
            }
            setSubmitted(true);
            setError("");
        } catch (err) {
            setError("Failed to submit. Please try again.");
            setSubmitted(true);
            setError("");
        }
    };

    return (
        <>
            <Box component="form" onSubmit={handleSubmit} sx={{ p: 2, }}>
                <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                    <Typography>Not Attending</Typography>
                    <Switch
                        checked={attending}
                        onChange={(e) => setAttending(e.target.checked)}
                        color="primary"
                        sx={{ mx: 1 }}
                        disabled={!isVotingAllowed()}
                    />
                    <Typography>Attending</Typography>
                </Box>

                {attending && (
                    <>
                        <Divider sx={{ my: 2 }} />
                        <FormControl component="fieldset" sx={{ mb: 2 }} error={!!error}>
                            <FormLabel component="legend">Meal Preference</FormLabel>
                            <RadioGroup
                                row
                                value={preference}
                                onChange={(e) => {
                                    setPreference(e.target.value as "Veg" | "Non-Veg");
                                    setError("");
                                }}
                            >
                                <FormControlLabel
                                    value="Veg"
                                    control={<Radio color="secondary" />}
                                    label={<Box sx={{ display: "flex", alignItems: "center" }}><RestaurantIcon color="secondary" sx={{ mr: 1 }} /><Typography>Vegetarian</Typography></Box>}
                                />
                                <FormControlLabel
                                    value="Non-Veg"
                                    control={<Radio />}
                                    label={<Box sx={{ display: "flex", alignItems: "center" }}><NoMeals color="primary" sx={{ mr: 1 }} /><Typography>Non-Vegetarian</Typography></Box>}
                                />
                            </RadioGroup>
                            {error && <Typography color="error" variant="caption">{error}</Typography>}
                        </FormControl>
                    </>
                )}

                <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    fullWidth
                    size="large"
                    sx={{ mt: 1 }}
                    onClick={handleSubmit}
                >
                    {submitted ? "Update" : "Submit"}
                </Button>
            </Box>
        </>
    );
};

export default LunchPollCard;
